export const IT_DASHBOARD_STATUSES = ["ACTIVE", "NOTICE PERIOD", "INACTIVE"];
export const LAPTOP_AGE_LIMIT_YEARS = 5;

export type ItAssetRow = {
    staffId: string;
    employeeId?: string;
    staffName?: string;
    staffStatus?: string;
    assetHostName?: string | null;
    o365?: string | null;
    assetCategory?: string | null;
    laptopYear?: string | null;
    model?: string | null;
    cs?: string | null;
    action1?: string | null;
};

export type ItStaffItem = ItAssetRow & { reasons?: string[]; ageYears?: number };

export type ItDashboardMetrics = {
    currentStaff: number;
    categories: { companyLaptop: number; personal: number; client: number; noLaptop: number; noRecord: number };
    attention: {
        crowdStrikeGaps: number;
        action1Gaps: number;
        ageingLaptops: number;
        assetsToRecover: number;
        licencesToRevoke: number;
    };
    o365: { basic: number; standard: number; noMailId: number; notSet: number };
    laptopYears: { year: string; count: number }[];
    securityGaps: ItStaffItem[];
    recoveries: ItStaffItem[];
    ageingLaptops: ItStaffItem[];
};

const ASSET_FIELDS = ["assetHostName", "o365", "assetCategory", "laptopYear", "model", "cs", "action1"] as const;
// "Exception" and "Client" are agreed alternatives to the agent being installed, not gaps.
const COVERED_VALUES = ["yes", "exception", "client"];
const PAID_LICENCES = ["basic", "standard"];

const norm = (value?: string | null) => (value ?? "").trim().toLowerCase();
export const isSecurityCovered = (value?: string | null) => COVERED_VALUES.includes(norm(value));
const byName = (a: ItAssetRow, b: ItAssetRow) => (a.staffName || "").localeCompare(b.staffName || "");

export function buildItDashboardMetrics(rows: ItAssetRow[], currentYear: number): ItDashboardMetrics {
    const result: ItDashboardMetrics = {
        currentStaff: 0,
        categories: { companyLaptop: 0, personal: 0, client: 0, noLaptop: 0, noRecord: 0 },
        attention: { crowdStrikeGaps: 0, action1Gaps: 0, ageingLaptops: 0, assetsToRecover: 0, licencesToRevoke: 0 },
        o365: { basic: 0, standard: 0, noMailId: 0, notSet: 0 },
        laptopYears: [],
        securityGaps: [],
        recoveries: [],
        ageingLaptops: [],
    };
    const yearCounts = new Map<string, number>();

    for (const row of rows) {
        const status = norm(row.staffStatus);
        const category = norm(row.assetCategory);
        const onCompanyLaptop = category === "rd laptop";
        const reasons: string[] = [];

        if (status === "inactive") {
            if (onCompanyLaptop) reasons.push("Laptop not returned");
            if (PAID_LICENCES.includes(norm(row.o365))) {
                reasons.push("O365 licence still assigned");
                result.attention.licencesToRevoke++;
            }
        }

        if (status === "active" || status === "notice period") {
            result.currentStaff++;
            if (onCompanyLaptop) result.categories.companyLaptop++;
            if (category === "personal") result.categories.personal++;
            if (category === "client") result.categories.client++;
            if (category === "no laptop") result.categories.noLaptop++;
            if (ASSET_FIELDS.every((field) => norm(row[field]) === "")) result.categories.noRecord++;

            const licence = norm(row.o365);
            if (licence === "basic") result.o365.basic++;
            if (licence === "standard") result.o365.standard++;
            if (licence === "no mail id") result.o365.noMailId++;
            if (licence === "") result.o365.notSet++;

            if (onCompanyLaptop) {
                const crowdStrikeGap = !isSecurityCovered(row.cs);
                const action1Gap = !isSecurityCovered(row.action1);
                if (crowdStrikeGap) result.attention.crowdStrikeGaps++;
                if (action1Gap) result.attention.action1Gaps++;
                if (crowdStrikeGap || action1Gap) result.securityGaps.push(row);

                const year = (row.laptopYear || "").trim();
                if (/^\d{4}$/.test(year)) {
                    yearCounts.set(year, (yearCounts.get(year) || 0) + 1);
                    const ageYears = currentYear - Number(year);
                    if (ageYears >= LAPTOP_AGE_LIMIT_YEARS) result.ageingLaptops.push({ ...row, ageYears });
                }

                if (status === "notice period") reasons.push("Collect laptop before last day");
            }
        }

        if (reasons.length) {
            result.recoveries.push({ ...row, reasons });
            if (onCompanyLaptop) result.attention.assetsToRecover++;
        }
    }

    result.attention.ageingLaptops = result.ageingLaptops.length;
    result.laptopYears = Array.from(yearCounts, ([year, count]) => ({ year, count })).sort((a, b) =>
        a.year.localeCompare(b.year)
    );
    result.securityGaps.sort(byName);
    result.ageingLaptops.sort((a, b) => (b.ageYears || 0) - (a.ageYears || 0) || byName(a, b));
    result.recoveries.sort(
        (a, b) => Number(norm(b.staffStatus) === "inactive") - Number(norm(a.staffStatus) === "inactive") || byName(a, b)
    );

    return result;
}
